import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { ZoomIn, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCroppedBlob, fileToDataUrl, type PixelCrop } from '@/utils/cropImage.ts';

interface AvatarCropperModalProps {
  file: File | null;
  onCancel: () => void;
  onSave: (blob: Blob) => Promise<void> | void;
}

/** LinkedIn-style avatar editor: drag to reposition, slider to zoom, round crop. */
export default function AvatarCropperModal({ file, onCancel, onSave }: AvatarCropperModalProps) {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<PixelCrop | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) return;
    let active = true;
    fileToDataUrl(file).then((url) => { if (active) setImageSrc(url); });
    return () => { active = false; };
  }, [file]);

  const onCropComplete = useCallback((_: unknown, pixels: PixelCrop) => setAreaPixels(pixels), []);

  const handleSave = async () => {
    if (!imageSrc || !areaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, areaPixels, 512);
      await onSave(blob);
    } finally {
      setSaving(false);
    }
  };

  if (!file) return null;

  // Portal to <body> so the overlay is centred on the viewport — rendering it
  // inside the header (which has backdrop-filter) would anchor `fixed` to the
  // header's containing block and clip the modal.
  return createPortal((
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-display text-xl text-foreground">{t('avatar.editTitle', 'Adatta la foto')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('avatar.editHint', 'Trascina per posizionare, usa lo slider per lo zoom.')}
          </p>
        </div>

        <div className="relative h-72 w-full bg-muted">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="flex items-center gap-3 px-5 py-4">
          <ZoomIn className="size-4 shrink-0 text-muted-foreground" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="h-1.5 w-full cursor-pointer accent-[var(--primary)]"
            aria-label={t('avatar.zoom', 'Zoom')}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            {t('common.cancel', 'Annulla')}
          </Button>
          <Button onClick={handleSave} disabled={saving || !areaPixels} className="gap-1.5">
            {saving && <Loader2 className="size-4 animate-spin" />}
            {t('avatar.save', 'Salva foto')}
          </Button>
        </div>
      </div>
    </div>
  ), document.body);
}
